import React, { useEffect, useMemo, useState } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import { buildPreviewImageUrl } from "../../../pages/puzzles/common/components/SocialShareDropdown";
import Logger from "../../../services/Logger";
import GenImagePreviewService from "../../system/gen-image-preview/GenImagePreviewService";

export function getPreviewPuzzleInstanceId(puzzle) {
    if (!puzzle) return "";
    const type = String(puzzle.type || "").toUpperCase();
    if (type === "CROSSWORD") {
        return puzzle.crossword?.id || "";
    }
    if (type === "WORD_SEARCH" || type === "HIDDEN_MESSAGE") {
        return puzzle.grid?.id || "";
    }
    return "";
}

export function resolvePreviewImageUrl(puzzle) {
    if (!puzzle) return "";
    const nested =
        puzzle.crossword?.previewImageUrl ||
        puzzle.grid?.previewImageUrl ||
        puzzle.previewImageUrl ||
        puzzle.template?.previewImageUrl ||
        "";
    if (nested) return nested;
    const instanceId = getPreviewPuzzleInstanceId(puzzle);
    if (!instanceId) return "";
    return buildPreviewImageUrl(instanceId, puzzle.type || "WORD_SEARCH");
}

function withCacheBust(url, token) {
    if (!url) return "";
    try {
        const parsed = new URL(url);
        parsed.searchParams.set("t", String(token));
        return parsed.toString();
    } catch {
        const sep = url.includes("?") ? "&" : "?";
        return `${url}${sep}t=${token}`;
    }
}

function readErrorMessage(error) {
    return error?.response?.message || error?.message || "Failed to regenerate preview image.";
}

const PreviewImageDialog = ({
    open,
    onClose,
    imageUrl,
    puzzleType,
    puzzleId,
    onRegenerated,
    onError,
}) => {
    const [cacheToken, setCacheToken] = useState(() => Date.now());
    const [displayUrl, setDisplayUrl] = useState(imageUrl || "");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setError(null);
        setLoading(false);
        setDisplayUrl(imageUrl || "");
        setCacheToken(Date.now());
    }, [open, imageUrl]);

    const src = useMemo(() => withCacheBust(displayUrl, cacheToken), [displayUrl, cacheToken]);
    const canRegenerate = Boolean(puzzleId && puzzleType);

    const handleRegenerate = async () => {
        if (!canRegenerate || loading) return;
        setLoading(true);
        setError(null);
        try {
            await GenImagePreviewService.generatePuzzleImage(puzzleType, puzzleId);
            let nextUrl = displayUrl || imageUrl || "";
            try {
                const puzzle = await GenImagePreviewService.getPuzzle(puzzleType, puzzleId);
                nextUrl =
                    puzzle?.previewImageUrl ||
                    puzzle?.template?.previewImageUrl ||
                    nextUrl;
            } catch (fetchError) {
                Logger.error("Error fetching regenerated preview image URL:", fetchError);
            }
            if (!nextUrl) {
                nextUrl = buildPreviewImageUrl(puzzleId, puzzleType);
            }
            setDisplayUrl(nextUrl);
            setCacheToken(Date.now());
            onRegenerated?.(nextUrl);
        } catch (e) {
            Logger.error("Error regenerating preview image:", e);
            const message = readErrorMessage(e);
            setError(message);
            onError?.(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>Preview Image</DialogTitle>
            <DialogContent sx={{ p: 2, textAlign: "center" }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
                        {error}
                    </Alert>
                )}
                {src ? (
                    <Box sx={{ position: "relative" }}>
                        {loading && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: "rgba(255,255,255,0.65)",
                                    zIndex: 1,
                                    borderRadius: 1,
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        )}
                        <img
                            key={cacheToken}
                            src={src}
                            alt="Puzzle Preview"
                            style={{
                                maxWidth: "100%",
                                height: "auto",
                                borderRadius: "8px",
                            }}
                        />
                    </Box>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No preview image yet. Generate one to see it here.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 2, pb: 2 }}>
                <Button onClick={onClose} disabled={loading}>
                    Close
                </Button>
                <Button
                    variant="contained"
                    onClick={handleRegenerate}
                    disabled={!canRegenerate || loading}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                >
                    {loading ? "Generating…" : "Re-generate"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PreviewImageDialog;
